import { Box, Button, Stack, Text } from '@chakra-ui/react'
import { faInfoCircle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'

import * as url from '@/client/services/url'

import ShwLink from '@/components/ShwLink'
import type { FormInputType } from '@/components/UI/Form/types'

import OnboardingInputRadio from './OnboardingInputRadio'

function wrapPronounLabel(label: string): React.ReactElement {
  if (typeof label !== 'string') {
    throw new Error('Invariant violation')
  }
  return (
    <Box width="140px" height="40px" px={2} py={2} textAlign={['left', 'center']} color="fg.muted">
      {label}
    </Box>
  )
}

const DEFAULT_ITEMS_TO_SHOW = 3

export default function OnboardingInputPronounsRadio({
  input,
}: {
  input: FormInputType
}): React.ReactElement {
  if (input.type !== 'radio') {
    throw new Error('Invariant violation')
  }

  const initialValueInExpanded = React.useMemo(
    () =>
      input.options
        .slice(DEFAULT_ITEMS_TO_SHOW)
        .map(option => option.value)
        .includes(`${input.initialValue}`),
    [input.initialValue, input.options]
  )
  const [expanded, setExpanded] = React.useState(initialValueInExpanded)

  const modifiedInput = {
    ...input,
    options: input.options.map(item => ({
      value: item.value,
      label: wrapPronounLabel(item.label as string),
    })),
  }
  if (!expanded) {
    modifiedInput.options = modifiedInput.options.slice(0, DEFAULT_ITEMS_TO_SHOW)
  }

  return (
    <Box>
      <OnboardingInputRadio input={modifiedInput} />
      {expanded ? null : (
        <Button variant="ghost" mt={3} onClick={() => setExpanded(true)}>
          See More Options
        </Button>
      )}
      {expanded ? (
        <Stack direction="column" spacing={4} mt={4}>
          <Stack
            direction="row"
            borderRadius="8px"
            py={2}
            px={6}
            backgroundColor="info.subtle"
            spacing={4}
            align="center"
          >
            <FontAwesomeIcon color={chakraTokenToVar('info.fg')} icon={faInfoCircle} />
            <Text textAlign="left" fontWeight={600}>
              We celebrate diversity and recognize that these options don&apos;t encompass the total
              range of identities! If you would like to use a different set of pronouns, please
              start a chat request from our{' '}
              <ShwLink
                href={url.faq.getUrl()}
                color="info.fg"
                _hover={{ color: 'info.fg', cursor: 'pointer' }}
              >
                Help Desk
              </ShwLink>
              .
            </Text>
          </Stack>
        </Stack>
      ) : null}
    </Box>
  )
}
